import React from 'react';
import { useTranslatedContent } from '../i18n/hooks/useTranslatedContent';
import { ArrowRight, Shield, Star, Sparkles } from 'lucide-react';
import Button from './common/Button';

const Hero = () => {
  const { t } = useTranslatedContent('hero');

  return (
    <div className="relative min-h-screen bg-gray-900 overflow-hidden flex items-center">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-violet-500/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-indigo-500/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 backdrop-blur-sm mb-8">
            <Sparkles className="h-4 w-4 text-blue-400 mr-2" />
            <span className="text-blue-200 text-sm font-medium">{t('badge')}</span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            {t('title')}{' '}
            <span className="relative">
              <span className="relative z-10 bg-gradient-to-r from-blue-400 to-violet-400 text-transparent bg-clip-text">
                {t('subtitle')}
              </span>
              <span className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-blue-400/50 to-violet-400/50 transform skew-x-12" />
            </span>
          </h1>

          {/* Description */}
          <p className="max-w-2xl mx-auto text-xl text-blue-100 mb-10">
            {t('description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              icon={ArrowRight}
              className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-lg px-8 py-4 border-0"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('buttons.getStarted')}
            </Button>
            <Button
              variant="secondary"
              className="bg-white/5 backdrop-blur-sm border-blue-400/20 text-blue-100 hover:bg-blue-800/50 text-lg px-8 py-4"
              onClick={() => document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('buttons.learnMore')}
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-20 pt-10 border-t border-blue-800/50">
            <p className="text-blue-200 mb-8">{t('trust.tagline')}</p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center space-x-2 text-blue-300">
                <Shield className="h-5 w-5" />
                <span>{t('trust.badges.iso')}</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300">
                <Star className="h-5 w-5" />
                <span>{t('trust.badges.enterprise')}</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300">
                <ArrowRight className="h-5 w-5" />
                <span>{t('trust.badges.support')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent" />
    </div>
  );
};

export default Hero;